import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSellerMode } from '../../contexts/SellerModeContext';
import {
  House, Package, ArrowsClockwise, Cube, Warehouse, ChartBar, FileText,
  Gear, Users, SignOut, IdentificationCard, List, X, Shield, Ticket, Star,
  TrendUp, ClockCounterClockwise, CaretDown, DotsThree, TreeStructure,
  CurrencyDollar, Storefront, Truck, Warning, Compass, FlowArrow, ChartLine, Gauge,
  UserPlus, CalendarBlank, Clock, MapPin, Timer, Buildings, Lightning,
  GraduationCap, ChatText, Tag, ClockAfternoon, Factory, PaintBrush,
  Receipt, Translate, Wrench, RocketLaunch, IdentificationBadge
} from '@phosphor-icons/react';

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { path: '/dashboard', icon: House, label: 'Dashboard', permission: 'dashboard.view', tooltip: 'Overview & KPIs' },
      { path: '/getting-started', icon: RocketLaunch, label: 'Getting Started', permission: 'dashboard.view', tooltip: 'Setup wizard' },
      { path: '/performance', icon: TrendUp, label: 'Performance', permission: 'dashboard.view', tooltip: 'Seller metrics & score' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/orders', icon: Package, label: 'Orders', permission: 'orders.view', tooltip: 'Manage orders' },
      { path: '/returns', icon: ArrowsClockwise, label: 'Returns', permission: 'returns.view', tooltip: 'RMA & refunds' },
      { path: '/products', icon: Cube, label: 'Products', permission: 'products.view', tooltip: 'Catalog management' },
      { path: '/inventory', icon: List, label: 'Inventory', permission: 'inventory.view', tooltip: 'Stock levels' },
      { path: '/warehouses', icon: Warehouse, label: 'Warehouses', permission: 'warehouses.view', tooltip: 'Fulfillment centers' },
      { path: '/shipping', icon: Truck, label: 'Shipping & Labels', permission: 'orders.view', tooltip: '3PL labels & manifests' },
      { path: '/bundles', icon: Package, label: 'Bundles & Kits', permission: 'products.view', tooltip: 'Product bundles' },
    ]
  },
  {
    label: 'Seller Config',
    items: [
      { path: '/staff', icon: UserPlus, label: 'Staff Management', permission: 'dashboard.view', tooltip: 'Internal team & roles' },
      { path: '/holidays', icon: CalendarBlank, label: 'Holidays', permission: 'dashboard.view', tooltip: 'Pause order processing' },
      { path: '/tat-config', icon: Clock, label: 'TAT & Cutoff', permission: 'dashboard.view', tooltip: 'Turnaround times' },
      { path: '/pincodes', icon: MapPin, label: 'Pincode Matrix', permission: 'dashboard.view', tooltip: 'Serviceable pincodes' },
      { path: '/slots', icon: Timer, label: 'Delivery Slots', permission: 'dashboard.view', tooltip: 'Time slot management' },
      { path: '/stores', icon: Buildings, label: 'Store Network', permission: 'dashboard.view', tooltip: 'Store hierarchy' },
      { path: '/quick-commerce', icon: Lightning, label: 'Quick Commerce', permission: 'dashboard.view', tooltip: 'Express delivery config' },
      { path: '/carrier-rules', icon: Truck, label: 'Carrier Rules', permission: 'dashboard.view', tooltip: 'Carrier selection & fallback' },
      { path: '/pricing-rules', icon: Tag, label: 'Pricing Rules', permission: 'dashboard.view', tooltip: 'Tiered & channel pricing' },
      { path: '/vendors', icon: Factory, label: 'B2B Vendors', permission: 'dashboard.view', tooltip: 'Vendor directory' },
      { path: '/invoice-templates', icon: Receipt, label: 'Invoice Templates', permission: 'dashboard.view', tooltip: 'Custom invoice designs' },
      { path: '/language-config', icon: Translate, label: 'Languages', permission: 'dashboard.view', tooltip: 'Multi-language support' },
      { path: '/seller-tools', icon: Wrench, label: 'Seller Tools', permission: 'dashboard.view', tooltip: 'PAN, Image, NOR/RRF, Capacity' },
      { path: '/seller-onboarding', icon: IdentificationBadge, label: 'Seller Onboarding', permission: 'dashboard.view', tooltip: 'Onboarding wizard' },
    ]
  },
  {
    label: 'Finance',
    items: [
      { path: '/reconciliation', icon: ChartBar, label: 'Reconciliation', permission: 'reconciliation.view', tooltip: 'Payments & settlements' },
      { path: '/reports', icon: FileText, label: 'Reports', permission: 'reports.view', tooltip: 'Business reports' },
      { path: '/scheduled-reports', icon: ClockAfternoon, label: 'Scheduled Reports', permission: 'reports.view', tooltip: 'Automated report delivery' },
      { path: '/analytics', icon: ChartLine, label: 'Analytics', permission: 'reports.view', tooltip: 'Advanced analytics & insights' },
    ]
  },
  {
    label: 'Communication',
    items: [
      { path: '/messages', icon: ChatText, label: 'Messages', permission: 'dashboard.view', tooltip: 'Buyer-seller messaging' },
      { path: '/training', icon: GraduationCap, label: 'Training', permission: 'dashboard.view', tooltip: 'Learning modules' },
      { path: '/tickets', icon: Ticket, label: 'Tickets', permission: 'dashboard.view', tooltip: 'Support requests' },
      { path: '/reviews', icon: Star, label: 'Ratings & Reviews', permission: 'dashboard.view', tooltip: 'Product, seller & logistics reviews' },
    ]
  },
  {
    label: 'Admin',
    items: [
      { path: '/admin/control-tower', icon: Compass, label: 'Control Tower', permission: 'roles.manage', adminOnly: true, tooltip: 'Marketplace analytics' },
      { path: '/admin/sellers', icon: Users, label: 'Seller Management', permission: 'roles.manage', adminOnly: true, tooltip: 'All seller details' },
      { path: '/admin/seller-comparison', icon: ArrowsClockwise, label: 'Seller Comparison', permission: 'roles.manage', adminOnly: true, tooltip: 'Compare seller metrics' },
      { path: '/admin/categories', icon: TreeStructure, label: 'Categories & Brands', permission: 'roles.manage', adminOnly: true, tooltip: 'Taxonomy management' },
      { path: '/admin/commissions', icon: CurrencyDollar, label: 'Commissions & Shipping', permission: 'roles.manage', adminOnly: true, tooltip: 'Fees, zones, settlements' },
      { path: '/admin/catalog', icon: Storefront, label: 'Product Catalog', permission: 'roles.manage', adminOnly: true, tooltip: 'All seller products' },
      { path: '/admin/penalties', icon: Warning, label: 'Seller Penalties', permission: 'roles.manage', adminOnly: true, tooltip: 'Penalties & deductions' },
      { path: '/admin/approval-workflows', icon: FlowArrow, label: 'Approval Workflows', permission: 'roles.manage', adminOnly: true, tooltip: 'Multi-level approvals' },
      { path: '/admin/performance-alerts', icon: TrendUp, label: 'Performance Alerts', permission: 'roles.manage', adminOnly: true, tooltip: 'Seller score monitoring' },
      { path: '/admin/branding', icon: PaintBrush, label: 'Branding', permission: 'roles.manage', adminOnly: true, tooltip: 'White-label customization' },
      { path: '/admin/onboarding', icon: IdentificationBadge, label: 'Onboarding & Approval', permission: 'roles.manage', adminOnly: true, tooltip: 'Seller onboarding applications' },
      { path: '/admin/kyc', icon: Shield, label: 'KYC Approvals', permission: 'roles.manage', adminOnly: true, tooltip: 'Review KYC applications' },
      { path: '/users', icon: Users, label: 'Access Control', permission: 'users.view', tooltip: 'Users & roles' },
      { path: '/kyc', icon: IdentificationCard, label: 'KYC', permission: 'kyc.view', tooltip: 'Verification documents' },
      { path: '/audit-logs', icon: ClockCounterClockwise, label: 'Audit Logs', permission: 'roles.manage', adminOnly: true, tooltip: 'Activity history' },
      { path: '/admin/ops', icon: Gauge, label: 'Ops Dashboard', permission: 'roles.manage', adminOnly: true, tooltip: 'API performance & rate limits' },
      { path: '/settings', icon: Gear, label: 'Settings', permission: 'settings.view', tooltip: 'Operating hours & holidays' },
    ]
  },
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout, hasPermission } = useAuth();
  const { isSellerMode, activeSeller } = useSellerMode();
  const navigate = useNavigate();

  // When impersonating a seller, show seller nav (not admin sections)
  const effectiveRole = isSellerMode ? 'seller' : user?.role;
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} data-testid="sidebar-overlay" />}

      <aside className={`fixed top-0 left-0 h-full w-[260px] bg-[#f9f9fb] z-50 flex flex-col transform transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-testid="sidebar">
        {/* Brand */}
        <div className="h-14 flex items-center justify-between px-5 flex-shrink-0">
          <img src="https://customer-assets.emergentagent.com/job_merchant-portal-112/artifacts/dsd8687t_Vinculum-logo-new.svg" alt="Vinculum" className="h-7" data-testid="sidebar-logo" />
          <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-[#efeff4]" data-testid="sidebar-close-btn">
            <X size={18} className="text-[#5a5c87]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-5">
          {NAV_SECTIONS.map(section => {
            const items = section.items.filter(item => {
              if (item.adminOnly && effectiveRole !== 'admin') return false;
              return hasPermission(item.permission);
            });
            if (items.length === 0) return null;
            return (
              <div key={section.label}>
                <p className="px-2 mb-1 text-[11px] font-medium text-[#9c9db7] uppercase tracking-[0.08em]">{section.label}</p>
                <div className="space-y-0.5">
                  {items.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      title={item.tooltip}
                      className={({ isActive }) => `flex items-center gap-2.5 px-2.5 py-[7px] text-[13px] font-medium rounded-lg transition-all duration-100 ${isActive ? 'bg-[#7d64ff] text-white' : 'text-[#3c3c64] hover:bg-[#efeff4]'}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon size={18} weight="regular" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="px-3 py-3 border-t border-[#e5e5ea] flex-shrink-0 relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg hover:bg-[#efeff4] transition-colors text-left"
            data-testid="user-profile-btn"
          >
            <div className="w-8 h-8 rounded-full bg-[#7d64ff] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-[#0A0A0A] truncate">{user?.name || 'Seller'}</p>
              <p className="text-[11px] text-[#9c9db7] truncate">{user?.email}</p>
            </div>
            <DotsThree size={18} className="text-[#9c9db7] flex-shrink-0" />
          </button>

          {profileOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-[#e5e5ea] rounded-xl shadow-lg overflow-hidden z-50" data-testid="profile-menu">
              <div className="px-4 py-3 border-b border-[#f0f0f0]">
                <p className="text-[13px] font-semibold text-[#0A0A0A]">{user?.name}</p>
                <p className="text-[11px] text-[#9c9db7]">{user?.email}</p>
                <div className="flex gap-1.5 mt-2">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-[#7d64ff]/10 text-[#7d64ff] rounded" title="User role">
                    {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                  </span>
                  {user?.kyc_status && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded ${user.kyc_status === 'approved' ? 'bg-[#28cd8c]/10 text-[#1fb076]' : 'bg-[#f6a609]/10 text-[#d18f08]'}`} title="KYC verification status">
                      KYC: {user.kyc_status}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-4 py-2.5 text-[13px] font-medium text-[#ea4b5a] hover:bg-[#ea4b5a]/5 transition-colors"
                data-testid="logout-btn"
              >
                <SignOut size={16} /> Sign out
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
